import { Decimal } from '@mlm/shared/money';
import type { ConsignmentCustomerDetail, ConsignmentDocRow, CustomerSoa } from './api';
import { type ExportTable, exportFilenameBase, localYmd } from '@mlm/ui/lib/export-table';

/**
 * Builders that turn each Consignment tab's already-loaded data into the
 * common `ExportTable`. No API calls: Holdings and Documents come from the
 * `['consignment-customer', userId]` query, the Statement from
 * `['consignment-soa', …]`, so an export is exactly what is on screen.
 *
 * Pure and unit-tested — the page passes a fixed `generatedAt` in tests.
 */

/** Document-type labels for the Documents tab. Single source: the page imports it too. */
export const DOC_LABEL: Record<ConsignmentDocRow['doc_type'], string> = {
  consignment: 'Consignment',
  billing: 'Billing',
  return: 'Return',
  payment: 'Payment',
};

/** The server caps the documents feed; say so rather than silently truncating. */
export const DOCS_CAP = 200;

export type ExportCtx = {
  consigneeName: string;
  generatedAt: Date;
};

const AMOUNTS_NOTE = 'Amounts in PHP';
const generatedLine = (d: Date) =>
  `Generated ${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(
    2,
    '0',
  )} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

/** Blank rather than a printed 0.00 — mirrors the on-screen table. */
const blankIfZero = (v: string): string | null => (new Decimal(v).isZero() ? null : v);

export function buildHoldingsExport(
  detail: ConsignmentCustomerDetail,
  ctx: ExportCtx,
): ExportTable {
  const rows = detail.holdings.map((h) => ({
    product: h.productName,
    variation: h.variantName ?? '',
    qty: h.qty,
    updatedAt: h.updatedAt,
  }));
  return {
    title: 'Consignment Holdings',
    subtitle: [`Consignee: ${ctx.consigneeName}`, generatedLine(ctx.generatedAt)],
    sheetName: 'Holdings',
    columns: [
      { header: 'Product', key: 'product', type: 'string' },
      { header: 'Variation', key: 'variation', type: 'string' },
      { header: 'Qty on hand', key: 'qty', type: 'number' },
      { header: 'Last movement', key: 'updatedAt', type: 'datetime' },
    ],
    rows,
    // `qty` is a count, not money — plain addition is correct here.
    totals: [{ product: 'Total', qty: rows.reduce((s, r) => s + r.qty, 0) }],
    filenameBase: exportFilenameBase(ctx.consigneeName, 'holdings', ctx.generatedAt),
  };
}

export function buildDocumentsExport(
  detail: ConsignmentCustomerDetail,
  ctx: ExportCtx,
): ExportTable {
  const capped = detail.docs.length >= DOCS_CAP;
  return {
    title: 'Consignment Documents',
    subtitle: [
      `Consignee: ${ctx.consigneeName}`,
      capped
        ? `Latest ${DOCS_CAP} documents only — older documents are not included`
        : `${detail.docs.length} document${detail.docs.length === 1 ? '' : 's'}`,
      AMOUNTS_NOTE,
      generatedLine(ctx.generatedAt),
    ],
    sheetName: 'Documents',
    columns: [
      { header: 'Date', key: 'date', type: 'datetime' },
      { header: 'Type', key: 'type', type: 'string' },
      { header: 'Doc No', key: 'docNo', type: 'string' },
      { header: 'Amount', key: 'amount', type: 'money' },
      { header: 'Status', key: 'status', type: 'string' },
      { header: 'Memo', key: 'memo', type: 'string' },
    ],
    rows: detail.docs.map((d) => ({
      date: d.sort_at ?? null,
      type: DOC_LABEL[d.doc_type] ?? d.doc_type,
      docNo: d.doc_no,
      amount: d.amount,
      status: d.status,
      memo: d.memo,
    })),
    // No totals: the rows mix consign-outs, billings, returns and payments.
    filenameBase: exportFilenameBase(ctx.consigneeName, 'documents', ctx.generatedAt),
  };
}

export function buildStatementExport(soa: CustomerSoa, ctx: ExportCtx): ExportTable {
  const period =
    soa.from || soa.to
      ? `Period: ${soa.from ? localYmd(soa.from) : 'Beginning'} – ${
          soa.to ? localYmd(soa.to) : localYmd(ctx.generatedAt)
        }`
      : `As of ${localYmd(ctx.generatedAt)}`;
  const contact = [soa.customer.email, soa.customer.phone].filter(Boolean).join(' · ');

  return {
    title: 'Statement of Account',
    subtitle: [
      `Consignee: ${ctx.consigneeName}`,
      ...(contact ? [contact] : []),
      period,
      AMOUNTS_NOTE,
      generatedLine(ctx.generatedAt),
    ],
    sheetName: 'Statement',
    columns: [
      { header: 'Date', key: 'date', type: 'date' },
      { header: 'Doc', key: 'doc', type: 'string' },
      { header: 'No', key: 'docNo', type: 'string' },
      { header: 'Ref', key: 'ref', type: 'string' },
      { header: 'Charges', key: 'charges', type: 'money' },
      { header: 'Payments', key: 'payments', type: 'money' },
      { header: 'Balance', key: 'balance', type: 'money' },
    ],
    rows: [
      { doc: 'Opening balance', balance: soa.openingBalance },
      ...soa.lines.map((l) => ({
        date: l.date,
        // `docType` arrives as a DISPLAY label already ('Billing', 'Payment',
        // 'Payment (void)') — never map it, or a voided receipt would print
        // as a live one.
        doc: l.docType,
        docNo: l.docNo,
        ref: l.ref,
        charges: blankIfZero(l.charges),
        payments: blankIfZero(l.payments),
        balance: l.balance,
      })),
    ],
    totals: [
      { doc: 'Totals', charges: soa.totalCharges, payments: soa.totalPayments },
      { doc: 'Closing balance', balance: soa.closingBalance },
    ],
    filenameBase: exportFilenameBase(
      ctx.consigneeName,
      'statement',
      ctx.generatedAt,
      soa.from && soa.to
        ? `${localYmd(soa.from).replace(/-/g, '')}-${localYmd(soa.to).replace(/-/g, '')}`
        : undefined,
    ),
  };
}
